import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import { useAtom } from "jotai";
import {
    ANIMATION_BUTTON_DURATION,
    ANIMATION_DURATION,
    SLIDE_TRANSITION,
} from "../constants/animations";
import { parentSectionAtom, sidebarOpenAtom } from "../state";
import { CollapseGlyph, Menu } from "./Menu";
import { useWindowSizeListener } from "@/ui/hooks/useWindowSize";

export type SidebarProps = {
    parentSections?: string[];
    testId?: string;
    className?: string;
    fixed?: boolean;
};

export function Sidebar({ parentSections, testId, className }: SidebarProps) {
    useWindowSizeListener();
    const OPEN_WIDTH = 364;
    const CLOSED_WIDTH = 40;
    const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenAtom);
    const [parentSection, setParentSection] = useAtom(parentSectionAtom);

    return (
        <LayoutGroup>
            <motion.div
                initial={false}
                animate={{
                    width: sidebarOpen ? OPEN_WIDTH : CLOSED_WIDTH,
                }}
                transition={SLIDE_TRANSITION}
                className="shrink-0"
            />
            <motion.div
                initial={false}
                animate={{
                    width: sidebarOpen ? OPEN_WIDTH : CLOSED_WIDTH,
                }}
                transition={SLIDE_TRANSITION}
                className={clsx(
                    "hide-scrollbar",
                    "flex flex-col",
                    "shrink-0 gap-4",
                    "fixed inset-y-5 left-4 z-[60]",
                    "h-[calc(100vh-40px)]",
                )}
            >
                <Menu
                    options={parentSections || []}
                    selectedValue={parentSection}
                    onValueChange={(value) => setParentSection(value)}
                />
                <motion.aside
                    className={clsx(
                        "border-default flex flex-col rounded-lg border bg-slate-800 px-3 py-5",
                        "shadow-search-bar",
                        "min-h-0 flex-1",
                        className,
                    )}
                    data-testid={testId}
                >
                    <div className="flex flex-col items-center">
                        <motion.div
                            className="shrink-0"
                            layout
                            initial={{
                                opacity: 0,
                                height: 0,
                                paddingTop: 0,
                            }}
                            animate={{
                                opacity: sidebarOpen ? 0 : 1,
                                height: sidebarOpen ? 0 : 20,
                                paddingTop: sidebarOpen ? 0 : 8,
                            }}
                            exit={{
                                opacity: 0,
                                height: 0,
                                paddingTop: 0,
                            }}
                            transition={{
                                duration: ANIMATION_BUTTON_DURATION,
                                ease: "easeInOut",
                                delay: sidebarOpen ? 0 : ANIMATION_DURATION,
                                opacity: {
                                    duration: 0,
                                    ease: "easeInOut",
                                    delay: sidebarOpen ? 0 : ANIMATION_DURATION,
                                },
                                paddingTop: {
                                    duration: 0,
                                    ease: "easeInOut",
                                    delay: ANIMATION_DURATION,
                                },
                            }}
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    setSidebarOpen(!sidebarOpen);
                                    e.currentTarget.blur();
                                }}
                                aria-label="Toggle sidebar"
                                className={clsx(
                                    "collapse-menu-btn cursor-pointer focus:outline-none",
                                )}
                            >
                                <CollapseGlyph className="size-4 cursor-pointer" />
                            </button>
                        </motion.div>
                    </div>

                    <motion.div
                        id="sidebar-header"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: sidebarOpen ? 1 : 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: ANIMATION_DURATION,
                            ease: "easeInOut",
                            delay: sidebarOpen ? ANIMATION_DURATION : 0,
                        }}
                        className={clsx(
                            "flex flex-col",
                            "min-h-0 gap-5",
                            !sidebarOpen && "pointer-events-none",
                        )}
                    />
                    <motion.div
                        id="sidebar-content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: sidebarOpen ? 1 : 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: ANIMATION_DURATION,
                            ease: "easeInOut",
                            delay: sidebarOpen ? ANIMATION_DURATION : 0,
                        }}
                        className={clsx(
                            "flex flex-col",
                            "min-h-0 flex-1 gap-5",
                            !sidebarOpen && "pointer-events-none",
                        )}
                    />
                </motion.aside>
            </motion.div>
        </LayoutGroup>
    );
}

export default Sidebar;
